import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { RefreshCw, RotateCw, ChevronRight, AlertTriangle, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../App";

const API = process.env.REACT_APP_BACKEND_URL;

const formatDate = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v).slice(0, 10);
  return d.toLocaleDateString("fr-FR");
};

const formatCurrency = (v) => {
  if (v === null || v === undefined) return "-";
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v);
};

/**
 * Widget: contrats CLIENT à surveiller (tacite reconduction, à confirmer, préavis proche)
 */
export default function ClientRenewalsWidget({ horizonDays = 90, compact = false }) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API}/api/contracts-renewals?horizon_days=${horizonDays}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRows(res.data.contracts || []);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, [token, horizonDays]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleScan = async () => {
    setRefreshing(true);
    try {
      const res = await axios.post(
        `${API}/api/contracts-renewals/auto-propose?force=true`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        res.data?.proposed
          ? `${res.data.proposed} contrat(s) client proposés en reconduction`
          : "Analyse terminée, aucune nouvelle proposition"
      );
      await fetchData();
    } catch (e) {
      toast.error("Erreur de scan");
    } finally {
      setRefreshing(false);
    }
  };

  const handleConfirm = async (row, e) => {
    e.stopPropagation();
    try {
      await axios.post(
        `${API}/api/contracts-renewals/${row.id}/confirm-renewal?duree_mois=${row.duree_reconduction_mois || 12}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Reconduction confirmée");
      await fetchData();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-4 animate-pulse">
        <div className="h-4 bg-slate-200 rounded w-1/3 mb-3"></div>
        <div className="h-16 bg-slate-100 rounded"></div>
      </div>
    );
  }

  if (rows.length === 0) return null;

  const toConfirm = rows.filter((r) => r.reconduction_state === "reconduction_a_confirmer");
  const active = rows.filter((r) => r.reconduction_state === "en_reconduction");
  const preavis = rows.filter((r) => !r.reconduction_state && r.jours_avant_preavis !== undefined && r.jours_avant_preavis <= horizonDays);

  return (
    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl border border-indigo-200 p-4" data-testid="client-renewals-widget">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <RotateCw className="w-5 h-5 text-indigo-600" />
          <div>
            <h3 className="font-semibold text-slate-800">Contrats client à surveiller</h3>
            <p className="text-xs text-slate-500 flex flex-wrap gap-x-2">
              {toConfirm.length > 0 && <span className="text-amber-700">⚠️ {toConfirm.length} à confirmer</span>}
              {active.length > 0 && <span className="text-purple-700">🔁 {active.length} en reconduction</span>}
              {preavis.length > 0 && <span>{preavis.length} préavis à venir</span>}
            </p>
          </div>
        </div>
        <button onClick={handleScan} disabled={refreshing} className="text-slate-500 hover:text-indigo-700 p-1" title="Relancer le scan" data-testid="client-widget-scan">
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
        </button>
      </div>

      <div className={`space-y-2 ${compact ? "max-h-72 overflow-y-auto" : ""}`}>
        {rows.slice(0, compact ? 6 : 30).map((r) => (
          <div
            key={r.id}
            className="bg-white rounded-lg border border-indigo-100 p-3 hover:border-indigo-300 cursor-pointer transition-colors"
            onClick={() => navigate(`/contrats/${r.id}`)}
            data-testid={`client-renewal-item-${r.id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {r.reconduction_state === "reconduction_a_confirmer" && (
                    <span className="text-xs px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded font-medium flex items-center gap-0.5">
                      <AlertTriangle className="w-3 h-3" /> À confirmer
                    </span>
                  )}
                  {r.reconduction_state === "en_reconduction" && (
                    <span className="text-xs px-1.5 py-0.5 bg-purple-100 text-purple-800 rounded font-medium">
                      🔁 Reconduit
                    </span>
                  )}
                  {!r.reconduction_state && r.jours_avant_preavis !== undefined && r.jours_avant_preavis <= 30 && (
                    <span className="text-xs px-1.5 py-0.5 bg-orange-100 text-orange-800 rounded font-medium">
                      Préavis {r.jours_avant_preavis}j
                    </span>
                  )}
                  <span className="font-medium text-slate-800 truncate">{r.societe || "?"}</span>
                </div>
                <div className="text-xs text-slate-600 mt-0.5 flex flex-wrap gap-x-3">
                  <span>{r.produit || r.type}</span>
                  <span>Fin: <strong>{formatDate(r.date_fin)}</strong></span>
                  {r.montant_abonnement && <span>{formatCurrency(r.montant_abonnement)}/mois</span>}
                  {r.leaser === "OUI" && r.numero_financement && (
                    <span className="text-indigo-600">Leaser: {r.numero_financement}</span>
                  )}
                </div>
                {r.nb_reconductions > 0 && (
                  <div className="text-xs text-purple-600 mt-0.5">
                    {r.nb_reconductions} reconduction{r.nb_reconductions > 1 ? "s" : ""} historique
                  </div>
                )}
              </div>
              <div className="flex flex-col items-end gap-1">
                {r.reconduction_state === "reconduction_a_confirmer" && (
                  <button
                    onClick={(e) => handleConfirm(r, e)}
                    className="text-xs px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-1"
                    data-testid={`quick-confirm-${r.id}`}
                  >
                    <CheckCircle className="w-3 h-3" /> Confirmer
                  </button>
                )}
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {compact && rows.length > 6 && (
        <button
          onClick={() => navigate("/contrats-annuaire?filter=reconduction")}
          className="w-full mt-3 text-xs text-indigo-700 hover:text-indigo-900 font-medium"
        >
          Voir tous les contrats à surveiller ({rows.length})
        </button>
      )}
    </div>
  );
}
